import MenuList from '@components/common/menu/MenuList';
import { buildContextMenuFromType } from '@components/map/contextMenu/builderFactory';
import { HasEventContext, HasMeasureContext, HasQrCodeContext } from '@components/map/contextMenu/builders/common';
import {
  AutomatonContextMenuContext,
  CabinetContextMenuContext,
  SensorContextMenuContext,
  SensorContextMenuContextCore,
} from '@components/map/contextMenu/builders/sensor';
import { useAutomatonCabinetSensorContextMenu } from '@components/map/contextMenu/sensor/useAutomatonCabinetSensorContextMenu';
import { useAutomatonSensorContextMenu } from '@components/map/contextMenu/sensor/useAutomatonSensorContextMenu';
import { useCameraSensorContextMenu } from '@components/map/contextMenu/sensor/useCameraSensorContextMenu';
import { FeatureTypeToContextComponentEnum } from '@components/map/contextMenu/type';
import { useMapContext } from '@components/map/MapContext';
import { createErrorToastFromTradId, ToastStatusEnum } from '@components/ui/toaster';
import { useAppSelector } from '@hooks/redux.hooks';
import { MeasureOriginEnum, useMeasureFromPointMenuItem } from '@hooks/useMeasureFromPointMenuItem';
import { useSelectorWithMapId } from '@hooks/useSelectorWithMapId';
import { useSelectorWithReplayMode } from '@hooks/useSelectorWithReplayMode';
import { useWithDispatch } from '@hooks/useWithDispatch';
import { hasRole, hasSensorRoleFromActiveProfile } from '@redux/authent/authent.selectors';
import { updatePopupByMapId } from '@redux/maps/maps.reducer';
import { selectPopupsByMapId } from '@redux/maps/maps.selectors';
import {
  selectSensorConfigurationByUniqueKey,
  selectSensorOrSubSensorInfoByUniqueKey,
} from '@redux/situation/monitoring.selectors';
import { selectSituationTime } from '@redux/situation/situation.selectors';
import { useUpdateSensorActivationMutation, useUpdateSensorMaintenanceMutation } from '@services/c2/c2.api';
import { findPopupByType, isOpenedPopupByType } from '@utils/popup.utils';
import { sensorFamilyFromSensorType } from '@utils/sensors/configuration.constants';
import { createSensorToast, isSubSensorUniqueKeys } from '@utils/sensors/sensors.utils';
import { toLngLat } from '@utils/validation/coordinates.utils';
import { ErrorBoundary } from 'react-error-boundary';

import { RoleEnum } from '@/types/authent/roles.types';
import { ActionEnum, Coordinates } from '@/types/commons/commons.types';
import { NotificationTypeEnum } from '@/types/data/data.types';
import { MapIdEnum, SensorUniqueKeys } from '@/types/map.types';
import { EventPopupData, HistoryPopupData, InfoDataTypeEnum, PopupType, PopupTypeEnum } from '@/types/popup.types';
import { SensorOrSubSensorConfiguration } from '@/types/sensor/configuration.types';
import { SensorTypeEnum, SubSensorTypeEnum } from '@/types/sensor/sensors';

interface OwnProps {
  clickPosition: { x: number; y: number };
  sensorUniqueKeys: SensorUniqueKeys;
  onClose: () => void;
}

const sensorTypeToMeasureOrigin = (sensorConfig: SensorOrSubSensorConfiguration | null): MeasureOriginEnum => {
  if (sensorConfig === null) {
    return MeasureOriginEnum.OTHER_SENSOR;
  }
  switch (sensorConfig.type) {
    case SensorTypeEnum.AUTOMATON:
      return MeasureOriginEnum.API;
    case SubSensorTypeEnum.AUTOMATON_CABINET:
      return MeasureOriginEnum.BT;
    case SensorTypeEnum.ZMER:
    case SensorTypeEnum.AF2206:
    case SensorTypeEnum.AXIS:
      return MeasureOriginEnum.CAMERA;
    default:
      return MeasureOriginEnum.OTHER_SENSOR;
  }
};

function SensorContextMenuContent({ clickPosition, sensorUniqueKeys, onClose }: Readonly<OwnProps>) {
  const { mapId } = useMapContext();
  const popups = useSelectorWithMapId(selectPopupsByMapId);
  const updatePopup = useWithDispatch(updatePopupByMapId);
  const situationTime = useAppSelector(selectSituationTime);

  const sensorOrSubSensorInfo = useSelectorWithReplayMode(
    selectSensorOrSubSensorInfoByUniqueKey,
    sensorUniqueKeys.sensorUniqueKey,
  );

  const configuration = sensorOrSubSensorInfo?.configuration ?? null;
  const status = sensorOrSubSensorInfo?.status ?? null;

  const maintainAction = {
    action: ActionEnum.MAINTAIN,
    sensorFamily: sensorOrSubSensorInfo?.type ? sensorFamilyFromSensorType[sensorOrSubSensorInfo.type] : undefined,
  };
  const hasRoleSensorMaintenance = useAppSelector((state) => hasSensorRoleFromActiveProfile(state, maintainAction));
  const accessEventCommand = useAppSelector((state) => hasRole(state, RoleEnum.CREATE_REPLAY));

  const parentConfiguration = useSelectorWithReplayMode(
    selectSensorConfigurationByUniqueKey,
    sensorUniqueKeys.parentUniqueKey,
  );

  const handleOpenPopup = (popupType: PopupTypeEnum, dataType?: InfoDataTypeEnum) => {
    const type = dataType ? { type: dataType } : {};
    updatePopup({
      mapId,
      popup: {
        type: popupType,
        position: { x: clickPosition.x, y: clickPosition.y },
        data: {
          ...type,
          featureValue: sensorUniqueKeys,
        },
      } as PopupType,
    });
    onClose();
  };

  const [isRoundOptionHidden, isRoundOptionDisabled] = useCameraSensorContextMenu(configuration, status);

  const {
    isSleepAllCommandLoading,
    isPowerOnCabinetsOptionDisabled,
    isPowerOffCabinetsOptionDisabled,
    onAutomatonSleepOptionAction,
    isAutomatonDefenceOptionLoading,
    isDisableCabinetsRepulsionOptionDisabled,
    isDisableCabinetsRepulsionOptionHidden,
    isEnableCabinetsRepulsionOptionDisabled,
    isEnableCabinetsRepulsionOptionHidden,
    onAutomatonDefenceOptionAction,
  } = useAutomatonSensorContextMenu(configuration, status);

  const automatonContext: AutomatonContextMenuContext = {
    isSleepAllCommandLoading,
    isPowerOnCabinetsOptionDisabled,
    isPowerOffCabinetsOptionDisabled,
    onAutomatonSleepOptionAction,
    isAutomatonDefenceOptionLoading,
    isDisableCabinetsRepulsionOptionDisabled,
    isDisableCabinetsRepulsionOptionHidden,
    isEnableCabinetsRepulsionOptionDisabled,
    isEnableCabinetsRepulsionOptionHidden,
    onAutomatonDefenceOptionAction,
  };

  const {
    isAckAlarmsLoading,
    isAckAlarmsDisabled,
    onAckAlarmsAction,
    isCabinetConnectionEnabled,
    isCabinetConnectionOptionLoading,
    isCabinetConnectionOptionDisabled,
    onCabinetConnectionOptionAction,
    isCabinetRepulsionOptionLoading,
    isDisableSegmentsRepulsionDisabled,
    isDisableSegmentsRepulsionHidden,
    isEnableSegmentsRepulsionDisabled,
    isEnableSegmentsRepulsionHidden,
    onCabinetRepulsionOptionAction,
  } = useAutomatonCabinetSensorContextMenu(configuration, status, parentConfiguration);

  const cabinetContext: CabinetContextMenuContext = {
    isAckAlarmsLoading,
    isAckAlarmsDisabled,
    onAckAlarmsAction,
    isCabinetConnectionEnabled,
    isCabinetConnectionOptionLoading,
    isCabinetConnectionOptionDisabled,
    onCabinetConnectionOptionAction,
    isCabinetRepulsionOptionLoading,
    isDisableSegmentsRepulsionDisabled,
    isDisableSegmentsRepulsionHidden,
    isEnableSegmentsRepulsionDisabled,
    isEnableSegmentsRepulsionHidden,
    onCabinetRepulsionOptionAction,
  };

  const isSubSensor = isSubSensorUniqueKeys(sensorUniqueKeys) && !!parentConfiguration;

  const isSensorActive = isSubSensor ? parentConfiguration.active && configuration?.active : configuration?.active;

  const isMaintenanceInherited = isSubSensor ? parentConfiguration.maintenance : false;
  const isSensorMaintenance = isMaintenanceInherited || configuration?.maintenance;

  const [patchActivation, { isLoading: isActivationLoading }] = useUpdateSensorActivationMutation();
  const [patchMaintenance, { isLoading: isMaintenanceLoading }] = useUpdateSensorMaintenanceMutation();

  const sensorHistoryPopup = findPopupByType(popups, PopupTypeEnum.HISTORY, NotificationTypeEnum.SENSOR);

  const sensorPosition: Coordinates | null =
    configuration && 'position' in configuration ? configuration.position : (configuration?.sensorPosition ?? null);

  const [label, enableMeasure] = useMeasureFromPointMenuItem({
    closeContextMenu: onClose,
    position: sensorPosition ?? null,
    measureOrigin: sensorTypeToMeasureOrigin(configuration),
  });

  if (!configuration) {
    return null;
  }

  const toggleActive = () => {
    let params;
    if (isSubSensor) {
      params = {
        id: parentConfiguration.id,
        site: parentConfiguration.site,
        appCode: parentConfiguration.appCode,
        active: !isSensorActive,
        type: configuration.type as SubSensorTypeEnum,
        codes: [configuration.code],
      };
    } else {
      params = {
        id: configuration.id,
        site: configuration.site,
        appCode: configuration.appCode,
        active: !isSensorActive,
      };
    }

    const toastParams = {
      commandValues: { state: !isSensorActive },
      command: 'sensors.commands.activationCommand',
      name: configuration.name,
    };
    createSensorToast({
      ...toastParams,
      toast: ToastStatusEnum.INFO,
    });

    patchActivation(params)
      .unwrap()
      .then(() =>
        createSensorToast({
          ...toastParams,
          toast: ToastStatusEnum.SUCCESS,
        }),
      )
      .catch(() =>
        createSensorToast({
          ...toastParams,
          toast: ToastStatusEnum.ERROR,
        }),
      );
  };

  const toggleMaintenance = () => {
    let params;
    if (isSubSensor) {
      params = {
        id: parentConfiguration.id,
        site: parentConfiguration.site,
        appCode: parentConfiguration.appCode,
        maintenance: !isSensorMaintenance,
        type: configuration.type as SubSensorTypeEnum,
        codes: [configuration.code],
      };
    } else {
      params = {
        id: configuration.id,
        site: configuration.site,
        appCode: configuration.appCode,
        maintenance: !isSensorMaintenance,
      };
    }

    const toastParams = {
      commandValues: { state: !isSensorMaintenance },
      command: 'sensors.commands.maintenanceCommand',
      name: configuration.name,
    };
    createSensorToast({
      ...toastParams,
      toast: ToastStatusEnum.INFO,
    });
    patchMaintenance(params)
      .unwrap()
      .then(() =>
        createSensorToast({
          ...toastParams,
          toast: ToastStatusEnum.SUCCESS,
        }),
      )
      .catch(() =>
        createSensorToast({
          ...toastParams,
          toast: ToastStatusEnum.ERROR,
        }),
      );
  };

  function handleHistoryPopup() {
    updatePopup({
      mapId,
      popup: {
        type: PopupTypeEnum.HISTORY,
        data: {
          identifiers: [configuration!.uniqueKey],
          sensorType: configuration!.type,
          sensorName: configuration!.name,
          sensorUniqueKeys: { sensorUniqueKey: configuration!.uniqueKey },
          type: NotificationTypeEnum.SENSOR,
          openingDate: situationTime ? new Date(situationTime) : new Date(),
        },
        position: { x: clickPosition.x, y: clickPosition.y },
      },
    });
    onClose();
  }

  const openGpsQrCodePopup = () => {
    if (sensorPosition) {
      updatePopup({
        mapId,
        popup: {
          type: PopupTypeEnum.GPS_QR_CODE,
          position: { x: clickPosition.x, y: clickPosition.y },
          data: {
            coordinates: sensorPosition,
          },
        },
      });
      onClose();
    }
  };

  const handleCreateEventClick = () => {
    updatePopup({
      mapId,
      popup: {
        type: PopupTypeEnum.EVENT,
        position: { x: clickPosition.x, y: clickPosition.y },
        data: {
          newEvent: {
            coordinates: sensorPosition,
            date: new Date().toISOString(),
          },
        } as EventPopupData,
      },
    });
    onClose();
  };

  const isInfoOptionDisabled: boolean =
    !!sensorHistoryPopup && (sensorHistoryPopup.data as HistoryPopupData).identifiers.includes(configuration.uniqueKey);

  const isHistoryOptionDisabled: boolean =
    !!sensorHistoryPopup && (sensorHistoryPopup.data as HistoryPopupData).identifiers.includes(configuration.uniqueKey);

  const eventContext: HasEventContext = {
    popups,
    accessEventCommand: accessEventCommand,
    isOpenedPopupByType,
    handleCreateEventClick,
  };

  const qrCodeContext: HasQrCodeContext = {
    position: toLngLat(sensorPosition),
    openGpsQrCodePopup,
  };
  const measureContext: HasMeasureContext = {
    position: toLngLat(sensorPosition),
    label,
    enableMeasure,
  };
  const sensorCoreContext: SensorContextMenuContextCore = {
    sensorType: configuration.type,
    isInfoOptionDisabled,
    handleOpenSensorInfo: () => handleOpenPopup(PopupTypeEnum.INFO, InfoDataTypeEnum.INFO_SENSOR),
    isSensorMaintenance: !!isSensorMaintenance,
    hasRoleSensorMaintenance,
    isMaintenanceLoading,
    isMaintenanceOptionDisabled: !isSensorActive || isMaintenanceInherited,
    isSensorActive: !!isSensorActive,
    isActivationLoading,
    isSubSensor,
    isHistoryOptionDisabled,
    isRoundOptionHidden,
    isRoundOptionDisabled,
    toggleMaintenance,
    toggleActive,
    handleHistoryPopup,
    onRoundOptionAction: () => handleOpenPopup(PopupTypeEnum.ROUND),
  };

  let ctx:
    | SensorContextMenuContext<SensorTypeEnum.AUTOMATON>
    | SensorContextMenuContext<SubSensorTypeEnum.AUTOMATON_CABINET>
    | SensorContextMenuContext<undefined>;

  if (configuration.type === SensorTypeEnum.AUTOMATON) {
    ctx = {
      ...sensorCoreContext,
      ...qrCodeContext,
      ...measureContext,
      ...eventContext,
      ...automatonContext,
    };
  } else if (configuration.type === SubSensorTypeEnum.AUTOMATON_CABINET) {
    ctx = {
      ...sensorCoreContext,
      ...qrCodeContext,
      ...measureContext,
      ...eventContext,
      ...cabinetContext,
    };
  } else {
    ctx = {
      ...sensorCoreContext,
      ...qrCodeContext,
      ...measureContext,
      ...eventContext,
    };
  }

  const options = buildContextMenuFromType<FeatureTypeToContextComponentEnum.SENSOR>(
    FeatureTypeToContextComponentEnum.SENSOR,
    mapId,
    ctx,
  );

  return <MenuList menuItems={options} resetNavigation />;
}

export default function SensorContextMenu(props: Readonly<OwnProps>) {
  const { mapId } = useMapContext();

  return mapId === MapIdEnum.REPLAY ? (
    <ErrorBoundary fallback={<></>} onError={() => createErrorToastFromTradId('error.sensorReplay.contextMenu')}>
      <SensorContextMenuContent {...props} />
    </ErrorBoundary>
  ) : (
    <SensorContextMenuContent {...props} />
  );
}
